import { Box } from "@mui/system";
import {
  Card,
  Fade,
  Grid,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { courseListSecondaryIcon } from "assets/images";
import projectTheme from "app.theme";
import React, { useEffect, useState } from "react";
import {
  BLENDED_FACE_TO_FACE_ONLINE,
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  TIME_VIEW_FORMAT,
  VIEW_TOOLTIP_TITLE,
} from "utils/constants/constant";
import {
  IEducatorHomeListMeta,
  IEnhancedTableProps,
  IMissingAttendanceCourse,
  IMissingAttendanceCourseDetails,
  IPastCoursesDetails,
} from "utils/interfaces/educator-home";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  convertToDateTimeWithFormat,
  getPagePayloadValuesWithId,
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { MissingAttendanceCourseListSuccessPayload } from "store/educatorHome/types";
import { useHistory } from "react-router-dom";
import NoRecords from "components/NoRecords";
import { AppRoutings } from "utils/enums/app-routings";
import CustomPagination from "components/customPagination";

const EnhancedTableHead = (props: IEnhancedTableProps) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IMissingAttendanceCourseDetails) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

const MissingAttendanceCourseList: React.FC<any> = (props) => {
  const history = useHistory();
  const [page, setPage] = useState<string>(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof IPastCoursesDetails>("course");
  const [missingAttendanceCourseList, setMissingAttendanceCourseList] =
    useState<IMissingAttendanceCourse[]>();
  const [missingAttendanceCourseListMeta, setMissingAttendanceCourseListMeta] =
    useState<IEducatorHomeListMeta>();

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(1);
  };

  const onPageChange = (e: any, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (
      event: React.MouseEvent<unknown>,
      newOrderBy: keyof IPastCoursesDetails
    ) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onPastCoursesListSuccess = (
    response: MissingAttendanceCourseListSuccessPayload
  ) => {
    setMissingAttendanceCourseList(response.missingAttendanceCourseList);
    setMissingAttendanceCourseListMeta(
      response.missingAttendanceCourseListPagination
    );
    hideLoaderForBatchApiCall();
  };

  const getMissingAttendanceCourseList = () => {
    const { missingAttendanceCourseListRequest } = props;

    if (missingAttendanceCourseListRequest) {
      showLoaderForBatchApiCall();

      const payload = {
        values: getPagePayloadValuesWithId(
          props?.userId,
          orderBy,
          order,
          pageNo,
          page
        ),
        callback: onPastCoursesListSuccess,
      };

      missingAttendanceCourseListRequest(payload);
    }
  };

  useEffect(() => {
    getMissingAttendanceCourseList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy]);

  return (
    <Grid item xs={12}>
      <Card>
        <Box className="table-card-header">
          <Box sx={{ justifyContent: "space-between", width: "100%" }}>
            <Typography variant="h3">
              My Past Courses With Missing Attendance
            </Typography>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e) => handleRequestSort(e, "course")}
                  fieldName="Session Name"
                  keyName="course"
                  sxStyle={{
                    width: "302px",
                    minWidth: "302px",
                    [projectTheme.breakpoints.down("lg")]: {
                      width: "210px",
                      minWidth: "210px",
                    },
                  }}
                />
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e) => handleRequestSort(e, "term")}
                  fieldName="Term"
                  keyName="term"
                  sxStyle={{
                    width: "110px",
                    minWidth: "110px",
                    [projectTheme.breakpoints.down("lg")]: {
                      width: "94px",
                      minWidth: "94px",
                    },
                  }}
                />
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e) => handleRequestSort(e, "location")}
                  fieldName="Location"
                  keyName="location"
                  sxStyle={{
                    width: "382px",
                    minWidth: "382px",
                  }}
                />
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e) => handleRequestSort(e, "startDate")}
                  fieldName="Date & Time of First Session"
                  keyName="startDate"
                  sxStyle={{
                    width: "255px",
                    minWidth: "255px",
                    [projectTheme.breakpoints.down("lg")]: {
                      width: "254px",
                      minWidth: "254px",
                    },
                  }}
                />
                <TableCell
                  align="center"
                  sx={{
                    width: "98px",
                    [projectTheme.breakpoints.down("sm")]: {
                      width: "90px",
                    },
                  }}
                >
                  Details
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {missingAttendanceCourseList?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row">
                    <Typography variant="body2" className="sco-list-word-wrap">
                      {row.courseName}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.term}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      className="location sco-list-word-wrap"
                    >
                      {row.location}
                      {row.isBlended && BLENDED_FACE_TO_FACE_ONLINE}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {" "}
                    {convertToDateTimeWithFormat(
                      row.startDateOfFirstSession,
                      DATE_VIEW_FORMAT
                    )}{" "}
                    {convertToDateTimeWithFormat(
                      row.startTimeOfFirstSession,
                      TIME_VIEW_FORMAT
                    )}{" "}
                    {"To"}{" "}
                    {convertToDateTimeWithFormat(
                      row.endTimeOfFirstSession,
                      TIME_VIEW_FORMAT
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div className="table-actions">
                      <Tooltip
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 600 }}
                        title={VIEW_TOOLTIP_TITLE}
                        arrow
                      >
                        <IconButton
                          onClick={() =>
                            history.push({
                              pathname: AppRoutings.EducatorsHomeDetails,
                              state: { scoId: row.id },
                            })
                          }
                          disabled={!props?.pagePermission?.edit}
                          className={
                            !props?.pagePermission?.edit ? "disabled-icon" : ""
                          }
                        >
                          <img src={courseListSecondaryIcon} alt="list" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {missingAttendanceCourseList &&
        missingAttendanceCourseList.length > 0 ? (
          <CustomPagination
            handleChange={handleChange}
            page={page}
            pageNo={pageNo}
            onPageChange={onPageChange}
            listMeta={missingAttendanceCourseListMeta}
          />
        ) : (
          <NoRecords />
        )}
      </Card>
    </Grid>
  );
};

export default MissingAttendanceCourseList;
